import {
  AssessmentEvaluationMatrix,
  BRAIN_DOMAIN_LEVEL,
  IAssessmentMatrix,
  IAssessmentEvaluationResponseData,
} from '@shared/models/matrix';
import { isNotEmptyEvaluation } from '@shared/utils/assessment';
import { EvaluationReviewStatus } from '@shared/models/assessmentdetails';
import { ASSESSMENT_DOMAINS } from '@shared/constants/assessmentdomains';
import {
  BrainFunctionEvaluationRating,
  EvaluationRatingValue,
  EVALUATIONS_COMPLETENESS,
} from '@shared/constants/assessments';

export class AssessmentEvaluationOverview {
  /** {domainId: {level: functionsCount}} */
  private _brainFunctionsData: Record<string, Record<number, number>>;
  constructor(
    public domainEvaluations: IAssessmentEvaluationResponseData[],
    public childAge: number,
    public assessmentMatrix?: IAssessmentMatrix,
    public isFirstAssessment?: boolean | null,
  ) {
    this._brainFunctionsData =
      this.assessmentMatrix?.brainFunctions?.reduce((acc, cur) => {
        return {
          ...acc,
          [cur.domain]: {
            ...(acc[cur.domain] ?? {}),
            [cur.level.brainLevelId]: (acc[cur.domain]?.[cur.level.brainLevelId] ?? 0) + 1,
          },
        };
      }, {}) ?? {};
  }

  public get Evaluations() {
    const result: BrainDomainLevelEvaluation[] = [];

    this.domainEvaluations.forEach(evaluations => {
      evaluations.levelEvaluations?.forEach(levelEvaluations => {
        result.push(
          new BrainDomainLevelEvaluation(
            evaluations.brainDomain,
            levelEvaluations.levelId,
            levelEvaluations.functionsEvaluations,
            levelEvaluations.markedAsNotYetAchievedBulk,
            evaluations.functionalAgeAtLevel8 || 0,
          ),
        );
      });
    });

    return result;
  }

  public get EvaluationMatrix(): AssessmentEvaluationMatrix {
    return (
      Object.entries(this._brainFunctionsData)?.reduce((acc, [domain, levelsData]) => {
        const domainEvaluation = this.domainEvaluations.find(d => d.brainDomain === domain);

        return {
          ...acc,
          [domain]: {
            domain,
            levels: Object.entries(levelsData).map(([level, funcCount]) => {
              const levelsEvaluations = domainEvaluation?.levelEvaluations
                ?.find(f => String(f.levelId) === String(level))
                ?.functionsEvaluations?.filter(
                  fe => fe.evaluationRating !== BrainFunctionEvaluationRating.NGA,
                );
              const levelsEvaluationCount = levelsEvaluations?.length ?? 0;

              const getCompletion = () => {
                if (Number(level) === BRAIN_DOMAIN_LEVEL.EIGHT) {
                  const levelRating = this._getLevelRating(domain, Number(level));

                  if (levelRating !== EVALUATIONS_COMPLETENESS.NYA) {
                    const level8EvaluationCount =
                      // if child age >= 72 then age child autofilled
                      domainEvaluation?.functionalAgeAtLevel8 && this.childAge < 72
                        ? levelsEvaluationCount + 1
                        : levelsEvaluationCount;
                    const level8FuncCount = this.childAge < 72 ? funcCount + 1 : funcCount;

                    return level8EvaluationCount === 0
                      ? 0
                      : Math.round((level8EvaluationCount * 100) / level8FuncCount) / 100;
                  }
                }

                return levelsEvaluationCount === 0
                  ? 0
                  : Math.round((levelsEvaluationCount * 100) / funcCount) / 100;
              };

              const completion = getCompletion();

              const requiredReview =
                levelsEvaluations?.some(
                  l =>
                    !(
                      l.reviewStatus === EvaluationReviewStatus.EvaluatorCompleted ||
                      l.reviewStatus === EvaluationReviewStatus.ReviewerCompleted
                    ),
                ) ?? true;

              return {
                level: Number(level),
                rating:
                  completion === 0
                    ? EVALUATIONS_COMPLETENESS.NC
                    : completion < 1
                      ? EVALUATIONS_COMPLETENESS.PC
                      : this._getLevelRating(domain, Number(level)),
                requiredReview: this.isFirstAssessment ? false : requiredReview,
                completion,
              };
            }),
          },
        };
      }, {}) ?? {}
    );
  }

  private _getLevelRating = (domain: string, level: number): EVALUATIONS_COMPLETENESS => {
    const evaluations =
      this.Evaluations.find(d => d.domainId === domain && `${d.levelId}` === `${level}`)
        ?.evaluations ?? [];

    const calculateCellRating = (evaluationsList: BrainFunctionEvaluation[]) => {
      const ratings = evaluationsList.filter(isNotEmptyEvaluation);
      const sumRatings = ratings.reduce(
        (acc, cur) => acc + EvaluationRatingValue[cur.evaluationRating],
        0,
      );
      const cellRating = sumRatings / ratings.length;

      switch (true) {
        case cellRating === 1:
          return EVALUATIONS_COMPLETENESS.A;
        case cellRating >= 0.75 && cellRating < 1:
          return EVALUATIONS_COMPLETENESS.NA;
        case cellRating > 0 && cellRating < 0.75:
          return EVALUATIONS_COMPLETENESS.PA;
        case cellRating === 0:
          return EVALUATIONS_COMPLETENESS.NYA;
      }

      return EVALUATIONS_COMPLETENESS.NC;
    };

    switch (true) {
      case level === BRAIN_DOMAIN_LEVEL.THREE &&
        domain === ASSESSMENT_DOMAINS.HEARING_UNDERSTANDING: {
        const ratings: number[] =
          evaluations.map(item => EvaluationRatingValue[item.evaluationRating]) ?? [];

        const lastItem = ratings?.[ratings.length - 1] ?? 0;
        const penultItem = ratings?.[ratings.length - 2] ?? 0;
        const sum = lastItem + penultItem;
        const isNYA = [...ratings].every(item => item === EvaluationRatingValue.NYA);

        if (isNYA) {
          return EVALUATIONS_COMPLETENESS.NYA;
        }

        if (sum < 1 || sum === 1) {
          return EVALUATIONS_COMPLETENESS.PA;
        }

        return calculateCellRating(evaluations);
      }

      case level === BRAIN_DOMAIN_LEVEL.FOUR && domain === ASSESSMENT_DOMAINS.EMOTIONAL_SOCIAL: {
        const ratings = evaluations.map(item => EvaluationRatingValue[item.evaluationRating]);
        const lastItem = ratings[ratings.length - 1];
        const isNYA = [...ratings].every(item => item === EvaluationRatingValue.NYA);

        if (isNYA) {
          return EVALUATIONS_COMPLETENESS.NYA;
        }

        if (lastItem === EvaluationRatingValue.NYA) {
          return EVALUATIONS_COMPLETENESS.PA;
        }

        return calculateCellRating(evaluations);
      }

      default: {
        return calculateCellRating(evaluations);
      }
    }
  };
}

export class BrainFunctionEvaluation {
  constructor(
    public brainFunctionId: number,
    public evaluationRating: string,
    public reviewStatus?: EvaluationReviewStatus | null,
  ) {}
}

class BrainDomainLevelEvaluation {
  constructor(
    public domainId: string,
    public levelId: number,
    public evaluations: BrainFunctionEvaluation[],
    public markedAsNotYetAchievedBulk: boolean,
    public functionalAgeAtLevel8: number,
  ) {}
}
